import {
  KnockFeedProvider,
  NotificationFeedPopover,
  useKnockClient,
  useNotifications,
  useNotificationStore,
} from "@knocklabs/react";
import { Badge, Stack } from "@mui/material";
import { Bell, BellRing } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { SidebarMenuButton, SidebarMenuItem, useSidebar } from "~/components/ui/shadcn/ui/sidebar";
import { config } from "~/config";
import { useI18n } from "~/lib/i18n/useI18n";

import "@knocklabs/react/dist/index.css";
import classNames from "classnames";

export const NotificationFeed = () => {
  const hasProvider = useHasKnockProvider();

  if (!config.knock.inAppFeedId) {
    console.warn("Knock is not configured");
    return null;
  }

  if (!hasProvider) {
    return null;
  }

  return (
    <KnockFeedProvider feedId={config.knock.inAppFeedId}>
      <NotificationButton feedId={config.knock.inAppFeedId} />
    </KnockFeedProvider>
  );
};

const useHasKnockProvider = () => {
  try {
    useKnockClient();
    return true;
  } catch (error) {
    return false;
  }
};

const NotificationButton: React.FC<{ feedId: string }> = ({ feedId }) => {
  const { t } = useI18n();
  const knockClient = useKnockClient();
  const feedClient = useNotifications(knockClient, feedId);
  const [isVisible, setIsVisible] = useState(false);
  const notifButtonRef = useRef<HTMLButtonElement>(null);
  const { open } = useSidebar();

  useEffect(() => {
    void feedClient.fetch();
  }, [feedClient]);

  const { metadata } = useNotificationStore(feedClient);
  const BellIcon = metadata.unseen_count > 0 ? BellRing : Bell;

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        ref={notifButtonRef}
        tooltip={t("components.layout.sidebar.notifications")}
        onClick={() => {
          setIsVisible(!isVisible);

          if (!isVisible) {
            void feedClient.markAllAsSeen();
          }
        }}
      >
        <BellIcon
          className={classNames({
            "rounded-full text-secondary-500 ring-2 ring-secondary-500 ring-offset-[3px] ring-offset-primary-100 group-hover:ring-offset-primary-200":
              !open && metadata.unseen_count > 0,
          })}
        />
        <Stack direction="row" alignItems="center" gap={2}>
          {t("components.layout.sidebar.notifications")}
          <Badge badgeContent={metadata.unseen_count} color="secondary" overlap="circular" className="ml-3" />
        </Stack>
      </SidebarMenuButton>

      <NotificationFeedPopover
        buttonRef={notifButtonRef}
        isVisible={isVisible}
        onClose={() => setIsVisible(false)}
        placement="right-end"
      />
    </SidebarMenuItem>
  );
};
